import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import SearchForm from "../../components/SearchForm";
import { URL, getToken, getLocalDate } from "../../utils";
import ApexChart from "../../components/ApexChart";
import CardProfessional from "../../components/CardProfessional";
import FilterModal from "../../components/FilterModal";
import ConditionalRender from "../../components/ConditionalRender";
import { usePermissions } from "../../hooks/usePermissions";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";

export default function Professionals() {
  const [loading, setLoading] = useState(false);
  const [professionals, setProfessionals] = useState([]);
  const [filteredProfessionals, setFilteredProfessionals] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(null);
  const [total, setTotal] = useState(0);
  const [statisticData, setStatisticData] = useState({
    week: {
      particuliers: { data: [], labels: [] },
      professionnels: { data: [], labels: [] },
    },
    month: {
      particuliers: { data: [], labels: [] },
      professionnels: { data: [], labels: [] },
    },
    year: {
      particuliers: { data: [], labels: [] },
      professionnels: { data: [], labels: [] },
    },
  });

  const { hasPermission } = usePermissions();
  const token = getToken();

  // Filtrage des clients professionnels
  const displayedProfessionals = useMemo(() => {
    const professionalsToFilter = isFiltered
      ? filteredProfessionals
      : professionals;
    if (!searchTerm) return professionalsToFilter;
    const searchTermLower = searchTerm.toLowerCase();
    return professionalsToFilter.filter(
      (professional) =>
        professional.nom_entreprise?.toLowerCase().includes(searchTermLower) ||
        professional.contact?.includes(searchTerm) ||
        professional.adresse?.toLowerCase().includes(searchTermLower) ||
        professional.secteur_activite
          ?.toLowerCase()
          .includes(searchTermLower) ||
        professional.registre_commerce?.toLowerCase().includes(searchTermLower),
    );
  }, [professionals, filteredProfessionals, searchTerm, isFiltered]);

  // Récupération paginée de la liste des professionnels
  const fetchProfessionals = useCallback(
    async (lastId = null) => {
      if (lastId == null) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      try {
        const params = lastId != null ? `?offset=${lastId}` : "";
        const response = await axios.get(
          URL + "Clients/clients-professionnels-list" + params,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
        const newProfessionals = response.data?.data ?? [];
        setProfessionals((prev) =>
          lastId == null ? newProfessionals : [...prev, ...newProfessionals],
        );
        setHasMore(response.data?.has_more ?? false);
        setNextOffset(response.data?.next_offset ?? null);
        setTotal(response.data?.total ?? 0);
      } catch (error) {
        toast.error("Erreur lors du chargement des données.");
        console.error("Erreur:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [token],
  );

  // Récupération des statistiques (graphiques) — une seule fois
  const fetchStatistics = useCallback(async () => {
    try {
      // Récupérer les statistiques
      const [weekDataResponse, monthDataResponse, yearDataResponse] =
        await Promise.all([
          axios.get(URL + "Clients/chiffres-affaire-clients_type_w", {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
          axios.get(URL + "Clients/chiffres-affaire-clients_type_m", {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
          axios.get(URL + "Clients/chiffres-affaire-clients_type_y", {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

      // Traitement des données de graphiques (semaine)
      const weekTemp = {
        particuliers: { data: [], labels: [] }, // Vide pour les professionnels
        professionnels: { data: [], labels: [] },
      };

      if (weekDataResponse.data.ventes_professionnels) {
        weekDataResponse.data.ventes_professionnels.forEach((item) => {
          const total = item.total || 0; // Assurer une valeur par défaut
          weekTemp.professionnels.data.push(total);
          const formattedDate = getLocalDate(
            new Date(item.date_sortie__date ?? ""),
          );
          weekTemp.professionnels.labels.push(formattedDate);
        });
      }

      // Traitement des données mensuelles
      const monthTemp = {
        particuliers: { data: [], labels: [] }, // Vide pour les professionnels
        professionnels: { data: [], labels: [] },
      };

      if (monthDataResponse.data.ventes_professionnels) {
        monthDataResponse.data.ventes_professionnels.forEach((item) => {
          const total = item.total || 0; // Assurer une valeur par défaut
          monthTemp.professionnels.data.push(total);
          const formattedDate = getLocalDate(new Date(item.date_sortie__date));
          monthTemp.professionnels.labels.push(formattedDate);
        });
      }

      // Traitement des données annuelles
      const yearTemp = {
        particuliers: { data: [], labels: [] }, // Vide pour les professionnels
        professionnels: { data: [], labels: [] },
      };

      if (yearDataResponse.data.ventes_professionnels) {
        yearDataResponse.data.ventes_professionnels.forEach((item) => {
          const total = item.total || 0; // Assurer une valeur par défaut
          yearTemp.professionnels.data.push(total);

          // Pour l'année, afficher uniquement le mois
          let dateToFormat = item.mois
            ? new Date(item.mois)
            : new Date(item.date_sortie__date || "");
          const monthName = dateToFormat.toLocaleString("fr-FR", {
            month: "long",
          });
          yearTemp.professionnels.labels.push(monthName);
        });
      }

      setStatisticData({
        week: weekTemp,
        month: monthTemp,
        year: yearTemp,
      });
    } catch (error) {
      toast.error("Erreur lors du chargement des données.");
      console.error("Erreur:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchProfessionals();
    fetchStatistics();
  }, [fetchProfessionals, fetchStatistics]);

  // Chargement de la page suivante lorsqu'on atteint le bas de la liste
  const loadMore = useCallback(() => {
    if (hasMore && !loadingMore && nextOffset != null) {
      fetchProfessionals(nextOffset);
    }
  }, [hasMore, loadingMore, nextOffset, fetchProfessionals]);

  const sentinelRef = useInfiniteScroll({
    hasMore: hasMore && !isFiltered && !searchTerm,
    loading: loading || loadingMore,
    onLoadMore: loadMore,
  });

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleApplyFilters = (filteredData) => {
    setFilteredProfessionals(filteredData);
    setIsFiltered(true);
  };

  const handleClearFilters = () => {
    setIsFiltered(false);
    setFilteredProfessionals([]);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-10 h-10 border-2 border-t-amber-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      <div className="shadow-md bg-white p-4 flex justify-between items-center sticky top-16 z-40">
        <SearchForm
          placeholder="Rechercher un client pro"
          onSearch={handleSearch}
        />

        <div className="flex items-center gap-2">
          {/* Bouton Filtres */}
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className="p-2 px-4 rounded-md bg-blue-500 text-white hover:bg-blue-600 focus:ring-2 border-2 border-blue-400 focus:ring-offset-2 focus:ring-blue-400 text-sm max-sm:hidden"
          >
            Filtres
          </button>

          {/* Bouton mobile pour filtres */}
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className="text-gray-600 hover:text-blue-500 focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-blue-100 focus:outline-none rounded-md p-1 sm:hidden"
          >
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
              />
            </svg>
          </button>

          {/* Bouton pour effacer les filtres si des filtres sont appliqués */}
          {isFiltered && (
            <button
              onClick={handleClearFilters}
              className="p-2 px-4 rounded-md bg-red-500 text-white hover:bg-red-600 focus:ring-2 border-2 border-red-400 focus:ring-offset-2 focus:ring-red-400 text-sm max-sm:hidden"
            >
              Effacer filtres
            </button>
          )}

          <ConditionalRender requiredPermissions="canCreateClient">
            <Link
              to="/clients/professionnels/add"
              className="p-2 px-4 rounded-md bg-amber-500 text-white hover:bg-amber-600 focus:ring-2 border-2 border-amber-400 focus:ring-offset-2 focus:ring-amber-400 text-sm max-sm:hidden"
            >
              Ajouter un client
            </Link>
            <Link
              to="/clients/professionnels/add"
              className="text-gray-600 max-sm:ml-10 hover:text-amber-500 focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-amber-100 focus:outline-none rounded-md p-1 sm:hidden"
            >
              <svg
                className="w-8 h-8"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  fillRule="evenodd"
                  d="M4.857 3A1.857 1.857 0 0 0 3 4.857v4.286C3 10.169 3.831 11 4.857 11h4.286A1.857 1.857 0 0 0 11 9.143V4.857A1.857 1.857 0 0 0 9.143 3H4.857Zm10 0A1.857 1.857 0 0 0 13 4.857v4.286c0 1.026.831 1.857 1.857 1.857h4.286A1.857 1.857 0 0 0 21 9.143V4.857A1.857 1.857 0 0 0 19.143 3h-4.286Zm-10 10A1.857 1.857 0 0 0 3 14.857v4.286C3 20.169 3.831 21 4.857 21h4.286A1.857 1.857 0 0 0 11 19.143v-4.286A1.857 1.857 0 0 0 9.143 13H4.857ZM18 14a1 1 0 1 0-2 0v2h-2a1 1 0 1 0 0 2h2v2a1 1 0 1 0 2 0v-2h2a1 1 0 1 0 0-2h-2v-2Z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
          </ConditionalRender>
        </div>
      </div>

      {/* Section Graphiques - Remplace le tableau */}
      <div className="p-4">
        <div className="grid gap-4 mx-auto w-full p-4 bg-white shadow-md rounded-md">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold">
              {
                {
                  week: "Variation du chiffre d'affaires des professionnels durant la semaine",
                  month:
                    "Variation du chiffre d'affaires des professionnels durant le mois",
                  year: "Variation du chiffre d'affaires des professionnels durant l'année",
                }[selectedPeriod]
              }
            </h2>

            <select
              className="w-max p-2 border-2 rounded-md bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
              name="selectedPeriod"
              id="selectedPeriod"
              value={selectedPeriod}
              onChange={(e) => {
                setSelectedPeriod(e.target.value);
              }}
            >
              <option value="week">Semaine</option>
              <option value="month">Mois</option>
              <option value="year">Année</option>
            </select>
          </div>

          <div className="flex items-center max-h-96">
            {statisticData[selectedPeriod] &&
              {
                week: (
                  <ApexChart
                    key={"week-" + Math.random()}
                    data={statisticData.week}
                    height={350}
                  />
                ),
                month: (
                  <ApexChart
                    key={"month-" + Math.random()}
                    data={statisticData.month}
                    height={350}
                  />
                ),
                year: (
                  <ApexChart
                    key={"year-" + Math.random()}
                    data={statisticData.year}
                    height={350}
                  />
                ),
              }[selectedPeriod]}
          </div>
        </div>
      </div>

      <div className="p-4 pt-0">
        <div className="text-2xl font-semibold flex justify-between items-center p-2">
          <h2>
            Professionnels ({displayedProfessionals.length}
            {!isFiltered && !searchTerm && total > 0 ? ` / ${total}` : ""})
            {isFiltered && (
              <span className="text-sm text-blue-600 font-normal ml-2">
                (Filtré
                {filteredProfessionals.length !== total
                  ? ` - ${total} total`
                  : ""}
                )
              </span>
            )}
          </h2>
          <div className="flex items-center gap-4">
            <Link
              to="/clients/professionals"
              className="flex items-center justify-center p-2 bg-white shadow-md rounded-md hover:bg-gray-100"
            >
              <svg
                className="w-6 h-6"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m15 19-7-7 7-7"
                />
              </svg>
            </Link>
            <Link
              to="#"
              className="flex items-center justify-center p-2 bg-white shadow-md rounded-md hover:bg-gray-100"
            >
              <svg
                className="w-6 h-6"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m9 5 7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>

        <div className="bg-white grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 p-4 overflow-auto">
          {displayedProfessionals.length > 0 ? (
            displayedProfessionals.map((item) => (
              <CardProfessional key={item.id} item={item} />
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              {searchTerm
                ? "Aucun client professionnel trouvé"
                : isFiltered
                  ? "Aucun client professionnel trouvé avec ces filtres"
                  : "Aucun client professionnel disponible"}
            </div>
          )}
        </div>

        {/* Sentinelle de défilement infini */}
        {!isFiltered && !searchTerm && hasMore && (
          <div ref={sentinelRef} className="flex justify-center py-6">
            {loadingMore && (
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
            )}
          </div>
        )}
      </div>

      {/* Modal de filtres */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        filterType="professionnels"
        title="Filtrer les clients professionnels"
      />
    </>
  );
}
